import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security, useOktaAuth } from '@okta/okta-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppConfig } from './AppConfigProvider';

export type AuthContext = {
  isAuthenticated?: boolean;
  accessToken?: string;
  logIn: () => Promise<void>;
  logOut: () => Promise<void>;
  user?: {
    displayName?: string;
    email?: string;
  };
};

type Props = {
  children: React.ReactNode;
};

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [oktaAuth, setOktaAuth] = React.useState<OktaAuth>();
  const appConfig = useAppConfig();
  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string) => {
    navigate(toRelativeUrl(originalUri, window.location.origin));
  };

  const clientId = appConfig?.oktaClientId;
  const issuerUri = appConfig?.oktaIssuerUri;
  React.useEffect(() => {
    setOktaAuth(new OktaAuth({
      issuer: issuerUri,
      clientId,
      redirectUri: `${window.location.origin}/login/callback`,
      scopes: ['openid', 'offline_access', 'email'],
      pkce: true,
    }));
  }, [clientId, issuerUri]);

  if (!oktaAuth) return null;

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {children}
    </Security>
  );
};

export const useAuth = (): AuthContext => {
  const auth = useOktaAuth();
  const [user, setUser] = React.useState<AuthContext['user']>();
  if (auth === undefined) {
    throw new Error('useAuth cannot be called outside AuthProvider');
  }
  const { oktaAuth, authState } = auth;
  React.useEffect(() => {
    oktaAuth.getUser().then((info) => setUser({
      displayName: info.preferred_username,
      email: info.email,
    }));
  }, [authState, oktaAuth]);
  return {
    isAuthenticated: auth.authState?.isAuthenticated,
    accessToken: auth.authState?.accessToken?.accessToken,
    logIn: async () => {
      if (!authState?.isAuthenticated) {
        const originalUri = toRelativeUrl(window.location.href, window.location.origin);
        oktaAuth.setOriginalUri(originalUri);
        await oktaAuth.signInWithRedirect();
      }
    },
    logOut: async () => {
      await oktaAuth.signOut();
    },
    user,
  };
};
